import React, { useState, useEffect } from 'react'
import { Row, Col, Form, Upload, Image, Spin } from 'antd'
import './styles/upload-doc.scss'
import type { UploadProps } from 'antd/es/upload/interface'
import pdfIcon from 'assets/Images/pdf.svg'
import Coverimage from 'assets/Images/Coverimage.svg'
import Icon from 'assets/Images/icon.svg'
import { useDispatch, connect } from 'react-redux'
import fileActions from 'Redux/FileUpload/action'
import { File as FileType, Photo } from 'types/globalTypes'
import { getFileType, resizeImageToJPEG } from 'helper/utility'
import { infoMessage } from 'api/global'
import heic2any from 'heic2any'
interface PropsTypes {
  documentIds: string[]
  setDocumentIds: (value: string[]) => void
  fileData: FileType
  multipleFileData: FileType[]
  loading: boolean
  multiple?: boolean
  docType?: 'image/*' | '.pdf,.doc,.docx,.txt'
  files?: FileType[] | Photo[]
  uploadMultipleAtOnce?: boolean
  coverPhotos?: boolean
  soruceType?:
    | 'guardian_profile'
    | 'user_profile'
    | 'organization_profile'
    | 'mentor_profile'
    | 'organizer_profile'
    | 'authorized_adult_profile'
    | 'kid_profile'
    | 'amenity'
    | 'live_update'
    | 'other'
    | 'icon'
}

const DocUpload: React.FC<PropsTypes> = ({
  documentIds,
  setDocumentIds,
  fileData,
  multipleFileData,
  loading,
  multiple,
  docType,
  files,
  uploadMultipleAtOnce,
  coverPhotos,
  soruceType,
}) => {
  const dispatch = useDispatch()
  const [galleryUrl, setGalleryUrl] = useState<any>([])
  const [docs, setDocs] = useState([])
  const propsForPhoto: UploadProps = {
    showUploadList: false,
    className: 'mt-100',

    onChange: async (info: any) => {
      if (uploadMultipleAtOnce) {
        // Only process new files that are being uploaded
        const newFiles = info.fileList.filter(
          (file: any) => file.status === 'uploading',
        )

        if (newFiles.length > 0) {
          // Clear previous multiple file data before starting new upload
          dispatch({
            type: fileActions.CLEAR_FILE_DATA,
          })

          let filePayload = []

          newFiles.forEach((file: any) => {
            if (file.status === 'uploading') {
              file.status = 'done'
            }

            if (file.status === 'done') {
              const fileObj = new FormData()
              fileObj.append('file', file.originFileObj)
              filePayload = [
                ...filePayload,
                {
                  title: file.name,
                  description: 'image_id',
                  altText: 'img',
                  sourceType: soruceType ?? 'other',
                },
              ]
            }
          })

          // Only dispatch with the new files, not the entire fileList
          dispatch({
            type: fileActions.REQUEST_MULTIPLE_FILE_UPLOAD,
            payload: newFiles,
          })
        }
      } else {
        if (info.file.status === 'uploading') {
          info.file.status = 'done'
        }
        try {
          dispatch({
            type: fileActions.CHANGE_FILE_TYPE,
          })

          let fileObj = new FormData()
          const fileType = getFileType(info.file.originFileObj)
          if (
            info.file.originFileObj.type === 'image/heic' ||
            info.file.originFileObj.name.endsWith('.heic')
          ) {
            try {
              const blob = await heic2any({
                blob: info.file.originFileObj,
                toType: 'image/jpeg',
                quality: 0.9,
              })
              const jpegFile = new File(
                [blob] as any,
                info.file.originFileObj.name.replace(/\.heic$/i, '.jpeg'),
                {
                  type: 'image/jpeg',
                },
              )
              fileObj.append('file', jpegFile)
            } catch (err) {
              infoMessage('HEIC conversion failed')
              return
            }
          } else if (fileType === 'image') {
            const jpegBlob = await resizeImageToJPEG(info.file.originFileObj)
            const jpegFile = new File([jpegBlob], `${info.file.name}.jpeg`, {
              type: 'image/jpeg',
            })
            fileObj.append('file', jpegFile)
          } else {
            fileObj.append('file', info.file.originFileObj)
          }
          let newFile = {
            title: 'name',
            description: 'img',
            altText: 'img',
            sourceType: soruceType ?? 'other',
          }
          dispatch({
            type: fileActions.REQUEST_FILE_UPLOAD,
            payload: fileObj,
            newFile: newFile,
          })
        } catch (error) {
          infoMessage('Something went wrong')
        }
      }
    },
    beforeUpload: async file => {
      let type = getFileType(file)

      if (file.type === 'image/heic' || file.name.endsWith('.heic')) {
        try {
          dispatch({ type: fileActions.FILE_CONVERSION_REQUEST })
          const blob = await heic2any({ blob: file, toType: 'image/jpeg' })
          const previewUrl = URL.createObjectURL(blob as any)
          setGalleryUrl(prev => [...prev, { url: previewUrl, type: 'image' }])
        } catch (err) {
          console.error('Preview conversion failed', err)
        }
      } else if (type === 'image') {
        const url = URL.createObjectURL(file)
        setGalleryUrl(prev => [...prev, { url, type: type[0] }])
      } else if (type === 'application') {
        setDocs(prev => [...prev, { name: file.name }])
      }
      return true
    },
  }

  const handleDeleteImage = (idx: number) => {
    const newImageData = galleryUrl.filter(
      (_: any, index: number) => index !== idx,
    )
    const newPhotoIds = documentIds.filter(
      (_: any, index: number) => index !== idx,
    )
    setGalleryUrl(newImageData)
    setDocumentIds(newPhotoIds)
  }

  const handleDeleteFile = (idx: number) => {
    const newFileData = docs.filter((_: any, index: number) => index !== idx)
    const newFileIds = documentIds.filter(
      (_: any, index: number) => index !== idx,
    )
    setDocs(newFileData)
    setDocumentIds(newFileIds)
  }

  useEffect(() => {
    if (multipleFileData.length > 0) {
      const ids = multipleFileData.map((file: FileType) => file._id)
      const uniqueIds = Array.from(new Set([...documentIds, ...ids]))
      setDocumentIds(uniqueIds)
      dispatch({
        type: fileActions.CLEAR_FILE_DATA,
      })
    }
    if (fileData?._id) {
      if (!coverPhotos) {
        setDocumentIds([...documentIds, fileData._id])
      }
      dispatch({
        type: fileActions.CLEAR_FILE_DATA,
      })
    }
  }, [fileData, multipleFileData])

  useEffect(() => {
    const fileTypes = ['.pdf', '.doc', '.docx', '.txt']
    if (files?.length > 0) {
      setGalleryUrl([]) // temporary fix do it later
      files.map((d: FileType | Photo) => {
        if (fileTypes.includes(d.ext)) {
          setDocs(prev => [...prev, { name: d.name }])
        } else {
          setGalleryUrl((prev: any) => [
            ...prev,
            { url: (d as Photo).post, type: (d as Photo).ext },
          ])
        }
      })
    }
  }, [files])

  const uploadButton = (
    <div>
      <>
        <div className="upload-acover-pic">
          <img src={Coverimage} alt="avatar" />
          {docType === '.pdf,.doc,.docx,.txt' ? 'Upload docs' : 'Upload image'}
        </div>
      </>
    </div>
  )

  return (
    <Row gutter={10}>
      <Col>
        <Form.Item name="galleryIds">
          <Row>
            <Col className="d-flex cvcv">
              <Upload
                {...propsForPhoto}
                listType="picture-card"
                accept={docType || 'image/*,.pdf,.doc,.docx,.txt'}
                multiple={multiple || false}
                disabled={
                  !multiple && (galleryUrl.length > 0 || docs.length > 0)
                }
                className="cover-img-step-5"
              >
                {uploadButton}
              </Upload>
            </Col>
          </Row>
        </Form.Item>
      </Col>

      <Col>
        {galleryUrl.length > 0 && (
          <Col className="antd-img-upload flex scroller gap-20 pb-10px">
            {galleryUrl.map((item: any, index: number) => {
              return (
                <div className="img-preview" key={index}>
                  <img
                    alt="#img"
                    className="closing-icon"
                    src={Icon}
                    onClick={() => handleDeleteImage(index)}
                  />
                  {/* <img
                    key={index}
                    className="br-12 "
                    height={100}
                    width={100}
                    src={item.url}
                  /> */}
                  <Image
                    key={index}
                    className="br-12"
                    height={100}
                    width={100}
                    src={item.url}
                    rootClassName={'mb-10'}
                  />
                </div>
              )
            })}
          </Col>
        )}
      </Col>

      <Col>
        {docs.length > 0 && (
          <Col className="antd-img-upload flex scroller gap-20">
            {docs.map((item: any, index: number) => {
              return (
                <div className="img-preview" key={index}>
                  <img
                    alt="#img"
                    className="closing-icon"
                    src={Icon}
                    onClick={() => handleDeleteFile(index)}
                  />
                  <div>
                    <img src={pdfIcon} alt="icon" width={50} height={50} />
                    <p>{item.name}</p>
                  </div>
                </div>
              )
            })}
          </Col>
        )}
      </Col>
    </Row>
  )
}

const mapStateToProps = (state: any) => ({
  eventsDataValue: state.events.eventsDataValue,
  eventOrganizationData: state.events.eventOrganizationData,
  fileData: state.fileUpload.fileData,
  multipleFileData: state.fileUpload.multipleFileData,
  loading: state.fileUpload.loading,
})
export default connect(mapStateToProps)(DocUpload)
